import { useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { z } from "zod"

import { standardEmail } from "@tfg/types/schemas/utils"

import {
  useForgotPasswordMutation,
  useLogin,
  useMagicLinkMutation,
  useUsernameMutation,
  useVerifyTwoFactorMutation,
} from "~/pages/Auth/hooks"
import { useAuth } from "~/store/auth-context"
import { showAxiosError } from "~/utils/showAxiosError"

const _usernameSchema = z.object({
  username: standardEmail(),
})

export type LoginState =
  | "username"
  | "password"
  | "magicLink"
  | "forgotPassword"
  | "resetPasswordEmailSent"
  | "magicLinkEmailSent"
  | "twoFactor"

/**
 * Login uses a small navigation stack so secondary steps (magic link,
 * forgot/set password) can return to the previous step without ad-hoc
 * return-state fields. Prefer XState if this flow grows more branches.
 */
export const useLoginFlow = () => {
  const navigate = useNavigate()
  const { authenticate, getHomeRedirectUrl } = useAuth()
  const [pageState, setPageState] = useState<LoginState>("username")
  const [returnStack, setReturnStack] = useState<LoginState[]>([])
  const [email, setEmail] = useState<string>("")
  const [userId, setUserId] = useState<string>("")
  const [canUseMagicLink, setCanUseMagicLink] = useState(false)
  const isSendingMagicLinkRef = useRef(false)

  const goTo = (next: LoginState): void => {
    setReturnStack((stack) => [...stack, pageState])
    setPageState(next)
  }

  const goBack = (): void => {
    const previous = returnStack[returnStack.length - 1] ?? "username"
    setReturnStack((stack) => stack.slice(0, -1))
    setPageState(previous)
  }

  const resetTo = (next: LoginState): void => {
    setReturnStack([])
    setPageState(next)
  }

  const {
    mutateAsync: login,
    isPending: isLoggingIn,
    error: loginError,
  } = useLogin()

  const { mutateAsync: verifyTwoFactor } = useVerifyTwoFactorMutation({
    onSuccess: (data) => {
      authenticate(data)
      navigate(getHomeRedirectUrl(data))
      resetTo("username")
    },
  })

  const {
    mutateAsync: sendResetPasswordEmail,
    isPending: isSendingResetPasswordEmail,
    error: forgotPasswordError,
  } = useForgotPasswordMutation({
    onSuccess: () => resetTo("resetPasswordEmailSent"),
  })

  const {
    mutateAsync: sendMagicLink,
    isPending: isSendingMagicLink,
    error: magicLinkError,
    reset: resetMagicLinkMutation,
  } = useMagicLinkMutation({
    onSuccess: () => resetTo("magicLinkEmailSent"),
  })

  const {
    mutateAsync: sendUsername,
    isPending: isSendingUsername,
    error: usernameError,
  } = useUsernameMutation({
    onSuccess(data) {
      setCanUseMagicLink(data.canUseMagicLink === true)
      if (data.nextStep === "password") {
        resetTo("password")
        return
      }
      // Unknown or magicLink: same choice screen (no existence leak).
      goTo("magicLink")
    },
    onError() {
      resetTo("username")
      setEmail("")
      setCanUseMagicLink(false)
    },
  })

  const handleUsernameSubmit = async ({
    username,
  }: z.infer<typeof _usernameSchema>) => {
    resetMagicLinkMutation()
    setEmail(username)
    await sendUsername({ username })
  }

  const handleLogin = async (values: {
    username: string
    password: string
    recaptchaToken?: string
  }) => {
    try {
      const response = await login(values)
      if (!response.twoFactor) {
        if (!response.user) throw new Error("User not found")
        authenticate(response.user)
        navigate(getHomeRedirectUrl(response.user))
        resetTo("username")
        return
      } else {
        if (!response.userId) throw new Error("User id not found")
        resetTo("twoFactor")
        setUserId(response.userId)
      }
    } catch (e) {
      console.error(e)
    }
  }

  const handleVerifyTwoFactor = async ({
    token,
    userId,
  }: {
    token: string
    userId: string
  }) => {
    try {
      await verifyTwoFactor({ token, userId })
    } catch (e) {
      console.error(e)
    }
  }

  const handleForgotPassword = async () => {
    try {
      await sendResetPasswordEmail({ email })
    } catch {
      // React Query sets forgotPasswordError; stay on forgotPassword.
    }
  }

  const handleGoToForgotPassword = (): void => {
    goTo("forgotPassword")
  }

  const requestMagicLink = async (): Promise<void> => {
    if (isSendingMagicLinkRef.current || isSendingMagicLink) return
    isSendingMagicLinkRef.current = true
    try {
      await sendMagicLink({ email })
    } catch (error) {
      // Password step uses a toaster so login ErrorBox stays clean.
      // Magic Link choice step still renders magicLinkError in ErrorBox.
      if (pageState === "password") {
        showAxiosError({
          error: error instanceof Error ? error : new Error(String(error)),
          showNotification: true,
        })
      }
    } finally {
      isSendingMagicLinkRef.current = false
    }
  }

  return {
    pageState,
    email,
    userId,
    canUseMagicLink,
    isLoggingIn,
    isSendingUsername,
    isSendingMagicLink,
    isSendingResetPasswordEmail,
    loginError,
    usernameError,
    magicLinkError,
    forgotPasswordError,
    handleUsernameSubmit,
    handleLogin,
    handleVerifyTwoFactor,
    handleForgotPassword,
    handleGoToForgotPassword,
    handleGoToSetPassword: handleGoToForgotPassword,
    handleUseMagicLink: requestMagicLink,
    handleSendMagicLink: requestMagicLink,
    handleBackFromMagicLink: goBack,
    handleBackToUsername: goBack,
  }
}
